import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useCalendarEvents } from "../hooks/useCalendarEvents";
import { buildApiUrl } from "../config";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import DevelopmentBanner from "../components/DevelopmentBanner";
import Footer from "../components/Footer";
import AppNavbar from "../components/AppNavbar";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  FileText,
  Gavel,
  Users,
  Bell,
  BellOff,
  Filter,
  LayoutGrid,
  List,
  RefreshCw,
  ExternalLink,
  X,
  Download,
  Columns,
  Search,
} from "lucide-react";

// Event type configurations
const EVENT_TYPES = {
  floor_session: { label: "Floor Session", color: "blue", icon: Users },
  committee_meeting: {
    label: "Committee Meeting",
    color: "purple",
    icon: Users,
  },
  calendar_release: {
    label: "Calendar Release",
    color: "amber",
    icon: FileText,
  },
  vote: { label: "Vote", color: "emerald", icon: Gavel },
  bill_action: { label: "Bill Action", color: "cyan", icon: FileText },
  local_meeting: { label: "Local Meeting", color: "indigo", icon: Users },
};

// Human-readable placement type labels
const PLACEMENT_TYPE_LABELS = {
  calendar: "On Calendar",
  general_calendar: "General Calendar",
  rules_calendar: "Rules Calendar",
  first_reader: "First Reading",
  second_reader: "Second Reading",
  third_reader: "Third Reading",
  agenda: "On Agenda",
  discussed: "Discussed",
  voted: "Voted",
  status_action: "Status Update",
  passed: "Passed",
  failed: "Failed",
  referred: "Referred",
};

// Status badge configurations
const STATUS_CONFIGS = {
  scheduled: {
    label: "Scheduled",
    bgClass: "bg-blue-50 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-300",
    borderClass: "border-blue-100 dark:border-blue-800/50"
  },
  confirmed: {
    label: "Confirmed",
    bgClass: "bg-emerald-50 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-300",
    borderClass: "border-emerald-100 dark:border-emerald-800/50"
  },
  occurred: {
    label: "Occurred",
    bgClass: "bg-gray-50 dark:bg-gray-800",
    textClass: "text-gray-700 dark:text-gray-300",
    borderClass: "border-gray-200 dark:border-gray-700"
  },
  canceled: {
    label: "Canceled",
    bgClass: "bg-red-50 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-300",
    borderClass: "border-red-100 dark:border-red-800/50"
  },
  skipped: {
    label: "Skipped",
    bgClass: "bg-amber-50 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-300",
    borderClass: "border-amber-100 dark:border-amber-800/50"
  },
  unknown: {
    label: "Unknown",
    bgClass: "bg-gray-50 dark:bg-gray-800",
    textClass: "text-gray-500 dark:text-gray-400",
    borderClass: "border-gray-200 dark:border-gray-700"
  },
};

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Utility functions

// Strip bill number prefix from titles like "HR1059: House Read and adopted" -> "House Read and adopted"
const stripBillPrefix = (title) => {
  if (!title) return title;
  // Match patterns like "HR1059: ", "SB123: ", "HB45: " at the start
  return title.includes(": ") ? title.split(": ").slice(1).join(": ") : title;
};

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const isSameDay = (d1, d2) => {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

// Calendar Grid Day Cell - Isolated, no cross-component bleeding
const DayCell = ({
  date,
  events,
  isCurrentMonth,
  isToday,
  isDarkMode,
  onDateClick,
  onEventClick,
  isWeekView,
}) => {
  const dayEvents = events.filter((e) => isSameDay(new Date(e.start_at), date));
  const maxVisible = isWeekView ? 8 : 3;
  const displayEvents = dayEvents.slice(0, maxVisible);
  const hasMore = dayEvents.length > maxVisible;

  return (
    <div
      onClick={() => onDateClick(date)}
      className={`relative p-2 border-b border-r cursor-pointer transition-colors overflow-hidden ${isWeekView ? "min-h-[400px]" : "min-h-[100px]"
        } ${isDarkMode ? "border-gray-700" : "border-gray-200"} ${isCurrentMonth
          ? isDarkMode
            ? "bg-gray-800 hover:bg-gray-750"
            : "bg-white hover:bg-gray-50"
          : isDarkMode
            ? "bg-gray-900/50 hover:bg-gray-800/50"
            : "bg-gray-50 hover:bg-gray-100"
        } ${isToday ? "ring-2 ring-inset ring-blue-500" : ""}`}
    >
      {/* Date number - isolated positioning */}
      <div
        className={`text-sm font-semibold mb-1.5 ${isToday
          ? "text-blue-600 dark:text-blue-400"
          : isCurrentMonth
            ? isDarkMode
              ? "text-white"
              : "text-gray-900"
            : isDarkMode
              ? "text-gray-600"
              : "text-gray-400"
          }`}
      >
        {date.getDate()}
      </div>

      {/* Events container - overflow hidden to prevent bleeding */}
      <div className="space-y-1 overflow-hidden">
        {displayEvents.map((event, idx) => {
          const config =
            EVENT_TYPES[event.event_type] || EVENT_TYPES.floor_session;
          const billCount = event.event_bills?.length || 0;

          // Compact label for grid view
          const shortLabel =
            billCount > 0
              ? `${billCount} bill${billCount > 1 ? "s" : ""}`
              : config.label.split(" ")[0]; // First word only

          const colorClasses = {
            blue: "bg-blue-600 hover:bg-blue-700",
            purple: "bg-purple-600 hover:bg-purple-700",
            amber: "bg-amber-600 hover:bg-amber-700",
            emerald: "bg-emerald-600 hover:bg-emerald-700",
            cyan: "bg-cyan-600 hover:bg-cyan-700",
            indigo: "bg-indigo-600 hover:bg-indigo-700",
          };

          return (
            <button
              key={event.id || idx}
              onClick={(e) => {
                e.stopPropagation();
                onEventClick(event);
              }}
              className={`w-full text-left text-xs px-1.5 py-1 rounded text-white font-medium truncate transition-colors ${colorClasses[config.color] || colorClasses.blue}`}
              title={`${event.title || config.label} - Click for details`}
            >
              <span className="opacity-75">{formatTime(event.start_at)}</span>
              <span className="mx-1">·</span>
              <span>{shortLabel}</span>
            </button>
          );
        })}

        {/* "More" indicator - clearly bounded */}
        {hasMore && (
          <div
            className={`text-xs font-medium px-1.5 py-0.5 rounded ${isDarkMode
              ? "text-gray-400 bg-gray-700/50"
              : "text-gray-500 bg-gray-100"
              }`}
          >
            +{dayEvents.length - maxVisible} more
          </div>
        )}
      </div>
    </div>
  );
};

// Event Card for Agenda View
const EventCard = ({ event, isDarkMode, onBillClick, onClick }) => {
  const config = EVENT_TYPES[event.event_type] || EVENT_TYPES.floor_session;
  const StatusIcon = config.icon;
  const statusConfig = STATUS_CONFIGS[event.status] || STATUS_CONFIGS.unknown;

  // Determine display title - strip bill number prefix for cleaner display
  const hasMultipleBills = event.event_bills && event.event_bills.length > 1;
  const baseTitle =
    stripBillPrefix(event.description) ||
    stripBillPrefix(event.title) ||
    config.label;
  const displayTitle = hasMultipleBills
    ? `${baseTitle} (${event.event_bills.length} bills)`
    : baseTitle;

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer ${isDarkMode
        ? "border-gray-700 hover:border-blue-600 bg-gray-800/50"
        : "border-gray-200 hover:border-blue-300 bg-white"
        }`}
    >
      {/* Color accent bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${config.color === "blue"
          ? "bg-blue-500"
          : config.color === "purple"
            ? "bg-purple-500"
            : config.color === "amber"
              ? "bg-amber-500"
              : config.color === "emerald"
                ? "bg-emerald-500"
                : config.color === "cyan"
                  ? "bg-cyan-500"
                  : "bg-indigo-500"
          }`}
      />

      <div className="p-4 pl-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div
              className={`p-2 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-100"
                }`}
            >
              <StatusIcon
                className={`w-4 h-4 ${config.color === "blue"
                  ? "text-blue-500"
                  : config.color === "purple"
                    ? "text-purple-500"
                    : config.color === "amber"
                      ? "text-amber-500"
                      : config.color === "emerald"
                        ? "text-emerald-500"
                        : config.color === "cyan"
                          ? "text-cyan-500"
                          : "text-indigo-500"
                  }`}
              />
            </div>
            <div>
              <span
                className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
              >
                {config.label}
              </span>
              {event.chamber && (
                <span
                  className={`ml-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  •{" "}
                  {event.chamber.charAt(0).toUpperCase() +
                    event.chamber.slice(1)}
                </span>
              )}
            </div>
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bgClass} ${statusConfig.textClass}`}
          >
            {statusConfig.label}
          </span>
        </div>

        {/* Title */}
        <h3
          className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {displayTitle}
        </h3>

        {/* Meta info */}
        <div className="flex flex-wrap gap-4 text-sm mb-3">
          <div
            className={`flex items-center gap-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <Clock className="w-4 h-4" />
            {formatDate(event.start_at)} at {formatTime(event.start_at)}
          </div>
          {event.location && (
            <div
              className={`flex items-center gap-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <MapPin className="w-4 h-4" />
              {event.location}
            </div>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <p
            className={`text-sm mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {event.description}
          </p>
        )}

        {/* Linked Bills */}
        {event.event_bills && event.event_bills.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <h4
              className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
            >
              {event.event_bills.length} Bills on Calendar
            </h4>
            <div className="flex flex-wrap gap-2">
              {event.event_bills.map((eb, idx) => {
                const placementLabel =
                  PLACEMENT_TYPE_LABELS[eb.placement_type] || eb.placement_type;
                return (
                  <Link
                    key={eb.bill_id || idx}
                    to={`/bills/${encodeURIComponent(eb.bills?.bill_number || eb.bills?.number || eb.bills?.id)}`}
                    state={{ returnUrl: "/calendar", fromCalendar: true }}
                    replace
                    onClick={(e) => e.stopPropagation()}
                    className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                      ? "bg-blue-900/40 text-blue-300 hover:bg-blue-800/60"
                      : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    {eb.bills?.bill_number ||
                      eb.bills?.number ||
                      eb.bill?.number ||
                      `Bill`}
                    {eb.placement_type && eb.placement_type !== "calendar" && (
                      <span
                        className={`ml-1.5 text-xs px-1.5 py-0.5 rounded ${isDarkMode ? "bg-gray-700/50" : "bg-white/50"
                          }`}
                      >
                        {placementLabel}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Source link */}
        {event.source_url && (
          <a
            href={event.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center mt-3 text-sm ${isDarkMode
              ? "text-blue-400 hover:text-blue-300"
              : "text-blue-600 hover:text-blue-700"
              }`}
          >
            View Source <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </a>
        )}
      </div>
    </div>
  );
};

// Filter Dropdown Component
const FilterDropdown = ({
  label,
  value,
  onChange,
  options,
  isDarkMode,
  icon: Icon,
}) => (
  <div>
    <label
      className={`flex items-center text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
    >
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-4 py-2.5 border rounded-xl text-sm transition-all ${isDarkMode
        ? "bg-gray-700 border-gray-600 text-white"
        : "bg-white border-gray-300 text-gray-900"
        } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// Main Calendar Component
const Calendar = ({ isDarkMode, setIsDarkMode, featureControls }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // View state
  const [viewMode, setViewMode] = useState("month"); // 'month' or 'agenda'
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get("date");
    return dateParam ? new Date(dateParam) : new Date();
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Committee state
  const [committees, setCommittees] = useState([]);
  const [loadingCommittees, setLoadingCommittees] = useState(false);

  // Search state for bills/events
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    chamber: "all",
    eventType: "all",
    body: "gga",
    committee: "all",
  });

  // Active event types for legend-based filtering (empty = all types shown)
  const [activeEventTypes, setActiveEventTypes] = useState(new Set());

  // Toggle an event type in the active filter set
  const toggleEventTypeFilter = (eventType) => {
    setActiveEventTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventType)) {
        newSet.delete(eventType);
      } else {
        newSet.add(eventType);
      }
      return newSet;
    });
  };

  // Clear all legend-based filters
  const clearEventTypeFilters = () => {
    setActiveEventTypes(new Set());
  };

  // Search for bills/events
  const handleSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      // Search bills
      const billsRes = await fetch(
        buildApiUrl(
          `/api/bills/v2?search=${encodeURIComponent(query)}&limit=10`,
        ),
      );
      const billsData = billsRes.ok ? await billsRes.json() : { bills: [] };

      // Search events by title
      const eventsRes = await fetch(
        buildApiUrl(
          `/api/calendar?search=${encodeURIComponent(query)}&limit=10`,
        ),
      );
      const eventsData = eventsRes.ok ? await eventsRes.json() : { events: [] };

      setSearchResults({
        bills: billsData.bills || [],
        events: eventsData.events || [],
      });
    } catch (err) {
      console.error("Search failed:", err);
      setSearchResults({ bills: [], events: [] });
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Fetch committees on mount
  React.useEffect(() => {
    const fetchCommittees = async () => {
      setLoadingCommittees(true);
      try {
        const res = await fetch(buildApiUrl("/api/committees?body=gga"));
        if (res.ok) {
          const data = await res.json();
          setCommittees(data.committees || []);
        }
      } catch (err) {
        console.error("Failed to fetch committees:", err);
      } finally {
        setLoadingCommittees(false);
      }
    };
    fetchCommittees();
  }, []);

  // Mock events data (will be replaced with API call)
  // Get calendar grid dates
  const calendarDates = useMemo(() => {
    // Week View Logic
    if (viewMode === "week") {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Sunday
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        dates.push(d);
      }
      return dates;
    }

    // Month View Logic
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = new Date(firstDay);
    startDay.setDate(startDay.getDate() - firstDay.getDay()); // Go back to Sunday

    const dates = [];
    const current = new Date(startDay);

    // Generate dates for the grid (always 6 weeks to handle max overflow)
    for (let i = 0; i < 42; i++) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }, [currentDate, viewMode]);

  // Get eventId from URL params (for deep linking to specific event)
  const eventIdParam = searchParams.get("eventId");

  // Fetch data with hook
  const { events, loading, error, refetch } = useCalendarEvents({
    start: calendarDates[0].toISOString(),
    end: calendarDates[calendarDates.length - 1].toISOString(),
    chamber: filters.chamber,
    eventType: filters.eventType,
    body: filters.body,
    committee: filters.committee,
  });

  // Auto-select event from URL param when events load
  React.useEffect(() => {
    if (eventIdParam && events.length > 0 && !selectedEvent) {
      const eventToSelect = events.find((e) => e.id === eventIdParam);
      if (eventToSelect) {
        setSelectedEvent(eventToSelect);
        // Clear the eventId param after opening
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("eventId");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [eventIdParam, events, selectedEvent, searchParams, setSearchParams]);

  // Filter client-side for immediate responsiveness if needed,
  // but API already filters. We might do derived filtering here if API doesn't cover everything.
  // Apply legend-based event type filtering on top of API results
  const filteredEvents = useMemo(() => {
    if (activeEventTypes.size === 0) {
      return events; // No legend filters active - show all
    }
    return events.filter((e) => activeEventTypes.has(e.event_type));
  }, [events, activeEventTypes]);

  // Group events by day for agenda view
  const groupedEvents = useMemo(() => {
    const groups = {};
    filteredEvents.forEach((event) => {
      const date = new Date(event.start_at);
      const dateStr = date.toISOString().split("T")[0]; // YYYY-MM-DD
      if (!groups[dateStr]) {
        groups[dateStr] = {
          date: date,
          dateStr: dateStr,
          events: [],
        };
      }
      groups[dateStr].events.push(event);
    });
    return Object.values(groups).sort((a, b) => a.date - b.date);
  }, [filteredEvents]);

  // Scroll to selected date when switching to agenda view
  useEffect(() => {
    if (viewMode === "agenda" && !loading && groupedEvents.length > 0) {
      const targetDateStr = currentDate.toISOString().split("T")[0];

      // Find the element for the target date, or the next available date if target date has no events
      const targetId = `date-${targetDateStr}`;
      let element = document.getElementById(targetId);

      // If no events on that specific day, find the next day with events
      if (!element) {
        const nextDayWithEvents = groupedEvents.find(group => group.dateStr > targetDateStr);
        if (nextDayWithEvents) {
          element = document.getElementById(`date-${nextDayWithEvents.dateStr}`);
        }
      }

      if (element) {
        // Use a small timeout to ensure content is fully rendered
        const timer = setTimeout(() => {
          const headerOffset = 0;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }, 150);
        return () => clearTimeout(timer);
      }
    }
  }, [viewMode, currentDate, loading, groupedEvents]);

  // Navigation handlers
  // Navigation handlers
  const navigatePrevious = () => {
    if (viewMode === "week") {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
      );
    }
  };

  const navigateNext = () => {
    if (viewMode === "week") {
      const d = new Date(currentDate);
      d.setDate(d.getDate() + 7);
      setCurrentDate(d);
    } else {
      setCurrentDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
      );
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date) => {
    setCurrentDate(date);
    setViewMode("agenda");
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleBillClick = (billId) => {
    navigate(`/bills/${billId}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const today = new Date();

  return (
    <div
      className={`min-h-screen w-full ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <AppNavbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} featureControls={featureControls} />

      <DevelopmentBanner isDarkMode={isDarkMode} />

      {/* Title and Controls Section */}
      <div className={`border-b ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Title Row - Centered */}
          <div className="text-center mb-4">
            <h1 className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-1 inline-flex items-center justify-center gap-2`}>
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#10b981]">
                <CalendarIcon className="w-5 h-5 text-white" />
              </div>
              Legislative Calendar
            </h1>
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Georgia General Assembly
            </p>
          </div>

          {/* Controls Row */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* Search input */}
            <div className="relative">
              <div className="relative">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search calendar by bills..."
                  className={`w-64 sm:w-80 md:w-96 pl-9 pr-8 py-2 rounded-lg border text-sm transition-all ${isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSearchResults(null);
                    }}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 rounded ${isDarkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"}`}
                  >
                    <X
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  </button>
                )}
              </div>
              {/* Search Results Dropdown */}
              {searchResults &&
                (searchResults.bills.length > 0 ||
                  searchResults.events.length > 0) && (
                  <div
                    className={`absolute top-full mt-1 right-0 w-80 max-h-96 overflow-y-auto rounded-xl shadow-xl border z-50 ${isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                      }`}
                  >
                    {isSearching && (
                      <div
                        className={`p-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Searching...
                      </div>
                    )}
                    {/* Bills Results */}
                    {searchResults.bills.length > 0 && (
                      <div
                        className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      >
                        <div
                          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400 bg-gray-700/50" : "text-gray-500 bg-gray-50"}`}
                        >
                          Bills ({searchResults.bills.length})
                        </div>
                        {searchResults.bills.map((bill) => (
                          <Link
                            key={bill.id}
                            to={`/bills/${encodeURIComponent(bill.bill_number)}`}
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults(null);
                            }}
                            className={`block px-3 py-2 transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"}`}
                          >
                            <div
                              className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {bill.bill_number}
                            </div>
                            <div
                              className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {bill.title}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                    {/* Events Results */}
                    {searchResults.events.length > 0 && (
                      <div>
                        <div
                          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400 bg-gray-700/50" : "text-gray-500 bg-gray-50"}`}
                        >
                          Events ({searchResults.events.length})
                        </div>
                        {searchResults.events.map((event) => (
                          <button
                            key={event.id}
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults(null);
                              setCurrentDate(new Date(event.start_at));
                              setSelectedEvent(event);
                            }}
                            className={`w-full text-left px-3 py-2 transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"}`}
                          >
                            <div
                              className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {event.title}
                            </div>
                            <div
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {formatDate(event.start_at)} •{" "}
                              {event.chamber
                                ? event.chamber.charAt(0).toUpperCase() +
                                event.chamber.slice(1)
                                : "GGA"}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.bills.length === 0 &&
                      searchResults.events.length === 0 &&
                      !isSearching && (
                        <div
                          className={`p-4 text-sm text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          No results found for "{searchQuery}"
                        </div>
                      )}
                  </div>
                )}
            </div>

            {/* View mode toggle */}
            <div
              className={`hidden sm:flex items-center rounded-lg p-1 ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}
            >
              <button
                onClick={() => setViewMode("month")}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${viewMode === "month"
                  ? "bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-sm"
                  : isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("week")}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${viewMode === "week"
                  ? "bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-sm"
                  : isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                  }`}
                title="Week View"
              >
                <Columns className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("agenda")}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${viewMode === "agenda"
                  ? "bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-sm"
                  : isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${showFilters
                ? "bg-blue-600 text-white"
                : isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </button>

            {/* Export Button */}
            <button
              onClick={() =>
                window.open(buildApiUrl("/api/calendar/export.ics"), "_blank")
              }
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              title="Export Calendar (ICS)"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div
          className={`border-b ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FilterDropdown
                label="Chamber"
                value={filters.chamber}
                onChange={(v) => handleFilterChange("chamber", v)}
                options={[
                  { value: "all", label: "All Chambers" },
                  { value: "house", label: "House" },
                  { value: "senate", label: "Senate" },
                ]}
                isDarkMode={isDarkMode}
                icon={Users}
              />
              <FilterDropdown
                label="Event Type"
                value={filters.eventType}
                onChange={(v) => handleFilterChange("eventType", v)}
                options={[
                  { value: "all", label: "All Events" },
                  { value: "floor_session", label: "Floor Sessions" },
                  { value: "committee_meeting", label: "Committee Meetings" },
                  { value: "vote", label: "Votes" },
                  { value: "bill_action", label: "Bill Actions" },
                ]}
                isDarkMode={isDarkMode}
                icon={CalendarIcon}
              />
              <FilterDropdown
                label="Legislative Body"
                value={filters.body}
                onChange={(v) => handleFilterChange("body", v)}
                options={[
                  { value: "gga", label: "Georgia General Assembly" },
                ]}
                isDarkMode={isDarkMode}
                icon={Gavel}
              />
            </div>
          </div>
        </div>
      )}

      {/* Calendar Navigation + Inline Legend */}
      <div
        className={`border-b ${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Left: Nav controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={navigatePrevious}
                className={`p-2 rounded-lg transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <ChevronLeft
                  className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                />
              </button>
              <h2
                className={`text-lg font-semibold min-w-[180px] text-center ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {viewMode === "week"
                  ? `Week of ${MONTHS[calendarDates[0]?.getMonth()]} ${calendarDates[0]?.getDate()}, ${calendarDates[0]?.getFullYear()}`
                  : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
              </h2>
              <button
                onClick={navigateNext}
                className={`p-2 rounded-lg transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <ChevronRight
                  className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                />
              </button>
              <button
                onClick={goToToday}
                className={`ml-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Today
              </button>
            </div>

            {/* Right: Inline Event Type Legend - Clickable Filters */}
            <div className="hidden md:flex items-center gap-2 text-xs">
              {activeEventTypes.size > 0 && (
                <button
                  onClick={clearEventTypeFilters}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                    }`}
                  title="Clear all filters"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}
              {Object.entries(EVENT_TYPES).filter(([key]) => key !== 'local_meeting').map(([key, config]) => {
                const isActive = activeEventTypes.has(key);
                const hasFilters = activeEventTypes.size > 0;
                const colorBgClasses = {
                  blue: isActive
                    ? "bg-blue-600 text-white shadow-sm ring-1 ring-blue-700"
                    : isDarkMode
                      ? "bg-blue-900/20 text-blue-400 border-blue-900/50"
                      : "bg-blue-50 text-blue-700 border-blue-100",
                  purple: isActive
                    ? "bg-purple-600 text-white shadow-sm ring-1 ring-purple-700"
                    : isDarkMode
                      ? "bg-purple-900/20 text-purple-400 border-purple-900/50"
                      : "bg-purple-50 text-purple-700 border-purple-100",
                  amber: isActive
                    ? "bg-amber-600 text-white shadow-sm ring-1 ring-amber-700"
                    : isDarkMode
                      ? "bg-amber-900/20 text-amber-400 border-amber-900/50"
                      : "bg-amber-50 text-amber-700 border-amber-100",
                  emerald: isActive
                    ? "bg-emerald-600 text-white shadow-sm ring-1 ring-emerald-700"
                    : isDarkMode
                      ? "bg-emerald-900/20 text-emerald-400 border-emerald-900/50"
                      : "bg-emerald-50 text-emerald-700 border-emerald-100",
                  cyan: isActive
                    ? "bg-cyan-600 text-white shadow-sm ring-1 ring-cyan-700"
                    : isDarkMode
                      ? "bg-cyan-900/20 text-cyan-400 border-cyan-900/50"
                      : "bg-cyan-50 text-cyan-700 border-cyan-100",
                  indigo: isActive
                    ? "bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-700"
                    : isDarkMode
                      ? "bg-indigo-900/20 text-indigo-400 border-indigo-900/50"
                      : "bg-indigo-50 text-indigo-700 border-indigo-100",
                };

                return (
                  <button
                    key={key}
                    onClick={() => toggleEventTypeFilter(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all border font-medium ${colorBgClasses[config.color] || colorBgClasses.blue
                      } ${hasFilters && !isActive ? "opacity-40 grayscale hover:opacity-100 hover:grayscale-0" : "shadow-sm"}`}
                    title={
                      isActive
                        ? `Remove ${config.label} filter`
                        : `Show only ${config.label}`
                    }
                  >
                    <div
                      className={`w-2 h-2 rounded-full shadow-sm ${isActive
                        ? "bg-white"
                        : config.color === "blue"
                          ? "bg-blue-500"
                          : config.color === "purple"
                            ? "bg-purple-500"
                            : config.color === "amber"
                              ? "bg-amber-500"
                              : config.color === "emerald"
                                ? "bg-emerald-500"
                                : config.color === "cyan"
                                  ? "bg-cyan-500"
                                  : "bg-indigo-500"
                        }`}
                    />
                    <span>{config.label}</span>
                    {isActive && <X className="w-3 h-3 ml-0.5" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw
              className={`w-8 h-8 animate-spin ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <CalendarIcon
              className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
            />
            <h3
              className={`text-lg font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
            >
              Unable to load calendar
            </h3>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
              {error}
            </p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Month/Week Grid View */}
            {(viewMode === "month" || viewMode === "week") && (
              <div
                className={`rounded-xl border overflow-hidden ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {/* Days header */}
                <div
                  className={`grid grid-cols-7 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <div
                      key={day}
                      className={`py-2 text-center text-sm font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                {/* Calendar grid */}
                <div className="grid grid-cols-7">
                  {calendarDates.map((date, idx) => (
                    <DayCell
                      key={idx}
                      date={date}
                      events={filteredEvents}
                      isCurrentMonth={
                        date.getMonth() === currentDate.getMonth()
                      }
                      isToday={isSameDay(date, today)}
                      isDarkMode={isDarkMode}
                      onDateClick={handleDateClick}
                      onEventClick={handleEventClick}
                      isWeekView={viewMode === "week"}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Agenda List View */}
            {viewMode === "agenda" && (
              <div className="space-y-4">
                {groupedEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon
                      className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                    />
                    <h3
                      className={`text-lg font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                    >
                      No events found
                    </h3>
                    <p
                      className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                    >
                      Try adjusting your filters or check back later.
                    </p>
                  </div>
                ) : (
                  groupedEvents.map((group) => (
                    <div key={group.dateStr} id={`date-${group.dateStr}`} className="space-y-3">
                      <div className={`sticky top-0 z-10 py-2 px-1 ${isDarkMode ? "bg-gray-900/90" : "bg-gray-50/90"} backdrop-blur-sm border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
                        <h3 className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                          {formatDate(group.date)}
                        </h3>
                      </div>
                      <div className="space-y-3">
                        {group.events.map((event) => (
                          <EventCard
                            key={event.id}
                            event={event}
                            isDarkMode={isDarkMode}
                            onBillClick={handleBillClick}
                            onClick={() => setSelectedEvent(event)}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Event Detail Modal - Dedicated layout, no component contamination */}
      {selectedEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className={`relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Color Bar */}
            {(() => {
              const config =
                EVENT_TYPES[selectedEvent.event_type] ||
                EVENT_TYPES.floor_session;
              const statusConfig =
                STATUS_CONFIGS[selectedEvent.status] || STATUS_CONFIGS.unknown;
              const StatusIcon = config.icon;
              const colorClass =
                config.color === "blue"
                  ? "bg-blue-500"
                  : config.color === "purple"
                    ? "bg-purple-500"
                    : config.color === "amber"
                      ? "bg-amber-500"
                      : config.color === "emerald"
                        ? "bg-emerald-500"
                        : config.color === "cyan"
                          ? "bg-cyan-500"
                          : "bg-indigo-500";

              return (
                <>
                  {/* Top Color Accent */}
                  <div className={`h-2 ${colorClass}`} />

                  {/* Header */}
                  <div
                    className={`px-6 py-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-3 rounded-xl ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
                        >
                          <StatusIcon
                            className={`w-6 h-6 ${config.color === "blue"
                              ? "text-blue-500"
                              : config.color === "purple"
                                ? "text-purple-500"
                                : config.color === "amber"
                                  ? "text-amber-500"
                                  : config.color === "emerald"
                                    ? "text-emerald-500"
                                    : config.color === "cyan"
                                      ? "text-cyan-500"
                                      : "text-indigo-500"
                              }`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {config.label}
                            </span>
                            {selectedEvent.chamber && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                              >
                                {selectedEvent.chamber.charAt(0).toUpperCase() +
                                  selectedEvent.chamber.slice(1)}
                              </span>
                            )}
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bgClass} ${statusConfig.textClass}`}
                          >
                            {statusConfig.label}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedEvent(null)}
                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div className="overflow-y-auto max-h-[calc(90vh-180px)] px-6 py-5">
                    {/* Title */}
                    <h2
                      className={`text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {stripBillPrefix(selectedEvent.description) ||
                        stripBillPrefix(selectedEvent.title) ||
                        config.label}
                      {selectedEvent.event_bills?.length > 1 && (
                        <span
                          className={`ml-2 text-base font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          ({selectedEvent.event_bills.length} bills)
                        </span>
                      )}
                    </h2>

                    {/* Date/Time/Location Grid */}
                    <div
                      className={`grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl mb-5 ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}
                    >
                      <div className="flex items-center gap-3">
                        <CalendarIcon
                          className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        <div>
                          <p
                            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            Date
                          </p>
                          <p
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {formatDate(selectedEvent.start_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock
                          className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        <div>
                          <p
                            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            Time
                          </p>
                          <p
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {formatTime(selectedEvent.start_at)}
                          </p>
                        </div>
                      </div>
                      {selectedEvent.location && (
                        <div className="flex items-center gap-3 sm:col-span-2">
                          <MapPin
                            className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          <div>
                            <p
                              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              Location
                            </p>
                            <p
                              className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {selectedEvent.location}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {selectedEvent.description && (
                      <div className="mb-5">
                        <h4
                          className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Description
                        </h4>
                        <p
                          className={`text-sm leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {selectedEvent.description}
                        </p>
                      </div>
                    )}

                    {/* Linked Bills - Clean grid layout */}
                    {selectedEvent.event_bills &&
                      selectedEvent.event_bills.length > 0 && (
                        <div className="mb-5">
                          <h4
                            className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                          >
                            <FileText className="w-4 h-4" />
                            Related Bills ({selectedEvent.event_bills.length})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {selectedEvent.event_bills.map((eb, idx) => {
                              const billNumber =
                                eb.bills?.bill_number ||
                                eb.bills?.number ||
                                eb.bills?.id ||
                                "Bill";
                              const placementLabel =
                                PLACEMENT_TYPE_LABELS[eb.placement_type] ||
                                eb.placement_type;
                              return (
                                <Link
                                  key={eb.bill_id || idx}
                                  to={`/bills/${encodeURIComponent(billNumber)}`}
                                  state={{
                                    returnUrl: "/calendar",
                                    fromCalendar: true,
                                  }}
                                  replace
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedEvent(null);
                                  }}
                                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all hover:scale-[1.02] ${isDarkMode
                                    ? "border-gray-700 bg-gray-800 hover:bg-gray-700 hover:border-blue-600"
                                    : "border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300"
                                    }`}
                                >
                                  <div
                                    className={`p-2 rounded-lg ${isDarkMode ? "bg-blue-900/40" : "bg-blue-100"}`}
                                  >
                                    <FileText
                                      className={`w-4 h-4 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p
                                        className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                      >
                                        {billNumber}
                                      </p>
                                      {eb.placement_type &&
                                        eb.placement_type !== "calendar" && (
                                          <span
                                            className={`text-xs px-1.5 py-0.5 rounded-full ${isDarkMode
                                              ? "bg-gray-700 text-gray-300"
                                              : "bg-gray-200 text-gray-600"
                                              }`}
                                          >
                                            {placementLabel}
                                          </span>
                                        )}
                                    </div>
                                    {eb.bills?.title && (
                                      <p
                                        className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                      >
                                        {eb.bills.title}
                                      </p>
                                    )}
                                  </div>
                                  <ChevronRight
                                    className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                  />
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}

                    {/* Source Link */}
                    {selectedEvent.source_url && (
                      <a
                        href={selectedEvent.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                          ? "bg-gray-800 text-blue-400 hover:bg-gray-700"
                          : "bg-gray-100 text-blue-600 hover:bg-gray-200"
                          }`}
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Original Source
                      </a>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
};

export default Calendar;
